/**
 * Single source of truth for SEO metadata + URL shape.
 *
 * Consumed by THREE places, which is why it is a dependency-free `.mjs` module:
 *   1. React      — src/hooks/useSeoMeta.js, src/components/Footer.js, the landing pages
 *   2. Edge       — functions/_middleware.js (bundled by wrangler/esbuild)
 *   3. Build      — scripts/generate-sitemap.mjs (plain Node ESM)
 *
 * It lives under src/ because CRA's ModuleScopePlugin forbids importing from
 * outside src/. It is `.mjs` rather than `.json` because JSON import assertions
 * (`with { type: 'json' }`) are spelled differently across Node versions,
 * webpack and esbuild — a plain ES module sidesteps that entirely.
 *
 * Keep this file free of imports and of any browser/Node-only globals.
 */

export const ORIGIN = 'https://app.tarotreadingcard.online';
export const DEFAULT_LOCALE = 'en';
export const LOCALES = ['en', 'ja', 'zh-TW'];

export const SITE_NAME = {
  en: 'TAROCA - Tarot Meaning & Reading Online Platform',
  ja: 'TAROCA - オンラインタロット意味・占いプラットフォーム',
  'zh-TW': 'TAROCA - 塔羅牌意義與占卜線上平台',
};

export const OG_IMAGE = {
  en: '/images/og/taroca_og.v2.jpg',
  ja: '/images/og/taroca_og_ja.v2.jpg',
  'zh-TW': '/images/og/taroca_og_zh.v2.jpg',
};

/**
 * `heroImage` is the LCP element of each landing page — a full-bleed CSS
 * background, which is the slowest asset the browser can discover (it must
 * parse the CSS *and* match the element first). The middleware preloads it.
 *
 * Desktop and mobile are separate encodes, so both are listed and the
 * middleware emits media-scoped preload links; a browser fetches only the one
 * matching its viewport. Paths are version-suffixed rather than content-hashed
 * so they are referenceable from here, from App.css, and from the edge.
 */
export const ROUTES = [
  {
    path: '/',
    match: 'exact',
    index: true,
    changefreq: 'weekly',
    priority: 1.0,
    heroImage: {
      desktop: '/images/hero/bg_day.v2.webp',
      mobile: '/images/hero/bg_day.mobile.v2.webp',
    },
    i18n: {
      en: {
        h1: 'Free Online Tarot Readings and Tarot Spreads',
        title: 'Free Online Tarot Readings & Tarot Spreads | TAROCA',
        description:
          'Draw free online tarot readings with TAROCA. Choose a love, career, personal growth or healing spread, get a clear interpretation, and save it to your tarot diary.',
      },
      ja: {
        h1: '無料オンラインタロット占いとスプレッド',
        title: '無料オンラインタロット占い・スプレッド | TAROCA',
        description:
          'TAROCAで無料のオンラインタロット占いを。恋愛・仕事・自己成長・癒しのスプレッドから選び、わかりやすい解釈を読んで、タロット日記に保存できます。',
      },
      'zh-TW': {
        h1: '免費線上塔羅牌占卜與牌陣',
        title: '免費線上塔羅牌占卜與牌陣 | TAROCA',
        description:
          '使用 TAROCA 進行免費線上塔羅牌占卜。從愛情、事業、個人成長與療癒牌陣中選擇，獲得清楚的解讀，並儲存到您的塔羅日記。',
      },
    },
  },
  {
    path: '/love-spread',
    match: 'exact',
    index: true,
    changefreq: 'monthly',
    priority: 0.9,
    heroImage: {
      desktop: '/images/hero/bg_love.v2.webp',
      mobile: '/images/hero/bg_love.mobile.v2.webp',
    },
    i18n: {
      en: {
        h1: 'Free Love Tarot Reading',
        title: 'Free Love Tarot Reading & Relationship Spreads | TAROCA',
        description:
          'Get a free love tarot reading online. Choose a relationship spread, draw your cards, and receive a clear interpretation of your love life from TAROCA.',
      },
      ja: {
        h1: '無料の恋愛タロット占い',
        title: '無料の恋愛タロット占い・人間関係スプレッド | TAROCA',
        description:
          'オンラインで無料の恋愛タロット占い。人間関係のスプレッドを選んでカードを引き、TAROCAが恋愛の流れをわかりやすく解説します。',
      },
      'zh-TW': {
        h1: '免費愛情塔羅牌占卜',
        title: '免費愛情塔羅牌占卜與感情牌陣 | TAROCA',
        description:
          '線上免費愛情塔羅牌占卜。選擇感情牌陣、抽出你的牌，TAROCA 將為你清楚解讀愛情與人際關係的走向。',
      },
    },
  },
  {
    path: '/career-spread',
    match: 'exact',
    index: true,
    changefreq: 'monthly',
    priority: 0.9,
    heroImage: {
      desktop: '/images/hero/bg_career.v2.webp',
      mobile: '/images/hero/bg_career.mobile.v2.webp',
    },
    i18n: {
      en: {
        h1: 'Career Tarot Reading and Spreads',
        title: 'Free Career Tarot Reading & Work Spreads | TAROCA',
        description:
          'Free career tarot reading online. Pick a work or purpose spread, draw your cards, and get practical guidance on your next professional step from TAROCA.',
      },
      ja: {
        h1: '無料の仕事タロット占い',
        title: '無料の仕事タロット占い・キャリアスプレッド | TAROCA',
        description:
          'オンラインで無料の仕事タロット占い。キャリアや目的のスプレッドを選んでカードを引き、次の一歩をTAROCAが具体的にアドバイスします。',
      },
      'zh-TW': {
        h1: '免費事業塔羅牌占卜',
        title: '免費事業塔羅牌占卜與工作牌陣 | TAROCA',
        description:
          '線上免費事業塔羅牌占卜。選擇工作或人生目標牌陣、抽出你的牌，TAROCA 提供下一步職涯方向的實用指引。',
      },
    },
  },
  {
    path: '/personal-growth-spread',
    match: 'exact',
    index: true,
    changefreq: 'monthly',
    priority: 0.9,
    heroImage: {
      desktop: '/images/hero/bg_personal_growth.v2.webp',
      mobile: '/images/hero/bg_personal_growth.mobile.v2.webp',
    },
    i18n: {
      en: {
        h1: 'Personal Growth Tarot Reading',
        title: 'Personal Growth Tarot Reading & Spreads | TAROCA',
        description:
          'Free personal growth tarot reading online. Choose a self-reflection spread, draw your cards, and explore your patterns and next steps with TAROCA.',
      },
      ja: {
        h1: '無料の成長タロット占い',
        title: '自己成長のタロット占い・内省スプレッド | TAROCA',
        description:
          'オンラインで無料の自己成長タロット占い。内省のスプレッドを選んでカードを引き、自分のパターンと次の一歩をTAROCAと一緒に見つめます。',
      },
      'zh-TW': {
        h1: '免費個人成長塔羅牌占卜',
        title: '個人成長塔羅牌占卜與自我探索牌陣 | TAROCA',
        description:
          '線上免費個人成長塔羅牌占卜。選擇自我探索牌陣、抽出你的牌，與 TAROCA 一起看見自己的模式與下一步。',
      },
    },
  },
  {
    path: '/mental-healing-spread',
    match: 'exact',
    index: true,
    changefreq: 'monthly',
    priority: 0.9,
    heroImage: {
      desktop: '/images/hero/bg_healing.v2.webp',
      mobile: '/images/hero/bg_healing.mobile.v2.webp',
    },
    // Deliberately non-medical wording per the audit: tarot is for reflection,
    // not medical advice. Do not reintroduce diagnostic/treatment language here.
    i18n: {
      en: {
        h1: 'Tarot for Reflection and Emotional Wellbeing',
        title: 'Tarot for Reflection & Emotional Wellbeing | TAROCA',
        description:
          'A free tarot spread for quiet reflection and emotional wellbeing. Draw your cards with TAROCA for gentle perspective. Tarot is for reflection, not medical advice.',
      },
      ja: {
        h1: 'こころを整えるタロット占い',
        title: 'こころを整えるタロット占い・内省のスプレッド | TAROCA',
        description:
          '静かに心を見つめるための無料タロットスプレッド。TAROCAでカードを引き、やさしい視点を得ましょう。タロットは内省のためのもので、医療的助言ではありません。',
      },
      'zh-TW': {
        h1: '沉澱心情的塔羅牌占卜',
        title: '沉澱心情的塔羅牌占卜與內省牌陣 | TAROCA',
        description:
          '免費的療癒與內省塔羅牌陣。與 TAROCA 一起抽牌，獲得溫柔的觀點。塔羅用於自我省思，並非醫療建議。',
      },
    },
  },
  {
    path: '/general-spread',
    match: 'exact',
    index: true,
    changefreq: 'monthly',
    priority: 0.9,
    heroImage: {
      desktop: '/images/hero/bg_day.v2.webp',
      mobile: '/images/hero/bg_day.mobile.v2.webp',
    },
    i18n: {
      en: {
        h1: 'Free Tarot Reading for General Guidance',
        title: 'Free Custom Tarot Spread & General Reading | TAROCA',
        description:
          'Build your own free tarot spread with TAROCA. Choose how many cards to draw, ask any question, and get a clear reading you can save to your tarot diary.',
      },
      ja: {
        h1: '一般・無料のタロット占い',
        title: '無料のカスタムタロットスプレッド・総合占い | TAROCA',
        description:
          'TAROCAで自分だけの無料タロットスプレッドを作成。引く枚数を選び、好きな質問をして、わかりやすい占い結果をタロット日記に保存できます。',
      },
      'zh-TW': {
        h1: '免費自訂塔羅牌占卜',
        title: '免費自訂塔羅牌陣與綜合占卜 | TAROCA',
        description:
          '使用 TAROCA 打造專屬的免費塔羅牌陣。自選抽牌張數、提出任何問題，獲得清楚的解讀並儲存到塔羅日記。',
      },
    },
  },

  // ---- Known but private: real 200, noindex,follow, never in the sitemap ----
  {
    path: '/login',
    match: 'exact',
    index: false,
    i18n: {
      en: { title: 'Sign in | TAROCA' },
      ja: { title: 'ログイン | TAROCA' },
      'zh-TW': { title: '登入 | TAROCA' },
    },
  },
  {
    path: '/reset-password',
    match: 'exact',
    index: false,
    i18n: {
      en: { title: 'Reset password | TAROCA' },
      ja: { title: 'パスワード再設定 | TAROCA' },
      'zh-TW': { title: '重設密碼 | TAROCA' },
    },
  },
  {
    path: '/profile',
    match: 'exact',
    index: false,
    i18n: {
      en: { title: 'Profile | TAROCA' },
      ja: { title: 'プロフィール | TAROCA' },
      'zh-TW': { title: '個人資料 | TAROCA' },
    },
  },
  {
    path: '/diary',
    match: 'exact',
    index: false,
    i18n: {
      en: { title: 'My Tarot Diary | TAROCA' },
      ja: { title: 'タロット日記 | TAROCA' },
      'zh-TW': { title: '我的塔羅日記 | TAROCA' },
    },
  },
  {
    path: '/import',
    match: 'exact',
    index: false,
    i18n: {
      en: { title: 'Import readings | TAROCA' },
      ja: { title: '占い結果のインポート | TAROCA' },
      'zh-TW': { title: '匯入占卜紀錄 | TAROCA' },
    },
  },
  {
    path: '/spread-drawing',
    match: 'exact',
    index: false,
    i18n: {
      en: { title: 'Drawing your cards | TAROCA' },
      ja: { title: 'カードを引く | TAROCA' },
      'zh-TW': { title: '抽牌中 | TAROCA' },
    },
  },
  {
    path: '/reading/:id',
    match: 'regex',
    pattern: '^/reading/[^/]+$',
    index: false,
    i18n: {
      en: { title: 'Your reading | TAROCA' },
      ja: { title: '占い結果 | TAROCA' },
      'zh-TW': { title: '你的占卜結果 | TAROCA' },
    },
  },
];

export const INDEXABLE_ROUTES = ROUTES.filter((r) => r.index);

/** Returns the route entry for a pathname, or null (→ genuine 404). */
export function matchRoute(pathname) {
  for (const route of ROUTES) {
    if (route.match === 'exact' && route.path === pathname) return route;
    if (route.match === 'regex' && new RegExp(route.pattern).test(pathname)) return route;
  }
  return null;
}

/** Normalises an arbitrary ?lang= value to a supported locale. */
export function normalizeLocale(value) {
  return LOCALES.includes(value) ? value : DEFAULT_LOCALE;
}

/**
 * Canonical URL policy: English is the bare URL, other locales carry ?lang=.
 * So /love-spread?lang=en canonicalises to /love-spread, collapsing the
 * duplicate and matching x-default.
 */
export function canonicalFor(path, locale) {
  return locale === DEFAULT_LOCALE ? `${ORIGIN}${path}` : `${ORIGIN}${path}?lang=${locale}`;
}

/**
 * Full reciprocal hreflang set for a path — every locale plus x-default.
 * Google discards the entire cluster unless each member lists an identical set
 * that includes itself, so this must always return all LOCALES + x-default.
 */
export function alternatesFor(path) {
  const alternates = LOCALES.map((locale) => ({
    hreflang: locale,
    href: canonicalFor(path, locale),
  }));
  alternates.push({ hreflang: 'x-default', href: `${ORIGIN}${path}` });
  return alternates;
}

/** Localised SEO strings for a pathname, falling back to English then null. */
export function getSeo(pathname, locale) {
  const route = matchRoute(pathname);
  if (!route) return null;
  const lang = normalizeLocale(locale);
  return route.i18n[lang] || route.i18n[DEFAULT_LOCALE] || null;
}

/** The H1 for a public landing page. Returns '' for routes without one. */
export function getH1(pathname, locale) {
  const seo = getSeo(pathname, locale);
  return (seo && seo.h1) || '';
}
